import { IconButton, keyframes, Tooltip, type TooltipProps } from '@chakra-ui/react';
import { ReloadIcon } from '@sphere/icons';
import { differenceInSeconds, formatDistance } from 'date-fns';
import { rem } from 'polished';
import { FC, MouseEventHandler, useEffect, useState, type ReactNode } from 'react';

const spin = keyframes`
  0% {
    transform: rotate(0deg);

  }
  80%, 100% {
    transform: rotate(360deg);
  }
`;

interface Props {
  isLoading: boolean;
  onClick: MouseEventHandler;
  tooltipProps?: Omit<TooltipProps, 'children'>;
  tooltipLabel?: (lastUpdated: string) => ReactNode;
  lastUpdated?: string | Date;
  size?: 'sm' | 'md';
}

const TIMER_INTERVAL = 1000; // ms

const RefreshButton: FC<Props> = ({
  isLoading,
  onClick,
  tooltipProps,
  tooltipLabel,
  lastUpdated,
  size = 'md',
}) => {
  const [lastUpdatedValue, setLastUpdated] = useState<Date>(
    lastUpdated ? new Date(lastUpdated) : new Date(),
  );
  const [difference, setDifference] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setDifference(differenceInSeconds(new Date(), lastUpdatedValue));
    }, TIMER_INTERVAL);

    return () => clearInterval(timer);
  }, [lastUpdatedValue]);

  const resetLastUpdated = () => setLastUpdated(new Date());

  return (
    <Tooltip
      label={tooltipLabel?.(formatDistance(0, difference * 1000))}
      placement="top"
      hasArrow
      arrowSize={8}
      isDisabled={!tooltipLabel}
      {...tooltipProps}
    >
      <IconButton
        aria-label="Refresh results"
        variant="secondary"
        aspectRatio={1}
        padding={
          size === 'sm'
            ? { base: 'space.10', md: 'space.12' }
            : { base: 'space.10', md: 'space.16' }
        }
        flexShrink={0}
        onClick={e => {
          onClick(e);
          resetLastUpdated();
        }}
        icon={
          <ReloadIcon
            boxSize={size === 'sm' ? rem(14) : { base: rem(16), md: rem(18) }}
            color={isLoading ? 'gray.5' : undefined}
            animation={isLoading ? `${spin} infinite 800ms ease-in-out` : undefined}
          />
        }
        isDisabled={isLoading}
        _disabled={{
          cursor: 'wait',
        }}
      />
    </Tooltip>
  );
};

export default RefreshButton;
